import React from 'react';
import { CheckIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Button } from '../../components/ui/Button';
import { Seo } from '../../components/Seo';
import { INTERESTS } from '../../data/academyCommunity';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import type { EmailPrefKey } from '../../types/academyHub';
const EMAIL_PREFS: {
  key: EmailPrefKey;
  label: string;
  detail: string;
}[] = [{
  key: 'enrolment',
  label: 'Enrolment confirmations',
  detail: 'When you join a course.'
}, {
  key: 'team',
  label: 'Team assignments',
  detail: 'When a team admin assigns you a course.'
}, {
  key: 'completion',
  label: 'Certificates',
  detail: 'When you complete a course.'
}, {
  key: 'forum',
  label: 'Forum replies',
  detail: 'When someone replies to your thread.'
}, {
  key: 'referral',
  label: 'Referrals',
  detail: 'When someone you invited enrols.'
}, {
  key: 'nudge',
  label: 'Reminders',
  detail: 'A nudge after seven days without learning.'
}];
export function AcademySettings() {
  const {
    state
  } = useAcademy();
  const {
    hub,
    setInterests,
    setEmailPref
  } = useAcademyHub();
  if (!state.learner) {
    return <Container className="py-20" data-id="element-1871" data-mp="1tmy2scrlorhq">
        <h1 className="font-display text-[30px] text-ink" data-id="element-1872" data-mp="1tlmx27elh9nz">Settings</h1>
        <p className="mt-3 text-[16px] text-body" data-id="element-1873" data-mp="1yl9cmnhwb1zy">Create a free account to choose your interests and notifications.</p>
        <Button to="/academy/dashboard" className="mt-6" data-id="element-1874" data-mp="1jro15q546m4h">
          Get started
        </Button>
      </Container>;
  }
  const toggleInterest = (id: (typeof INTERESTS)[number]['id']) => setInterests(hub.interests.includes(id) ? hub.interests.filter(x => x !== id) : [...hub.interests, id]);
  return <>
      <Seo title="Settings, Cavalla Academy" description="Your Academy settings." path="/academy/settings" noIndex data-id="element-1875" data-mp="352whox9gm7qv" />
      <Container className="max-w-[860px] py-12 sm:py-16" data-id="element-1876" data-mp="3a2iux6pafnzo">
        <h1 className="font-display text-[30px] leading-tight text-ink sm:text-[38px]" data-id="element-1877" data-mp="39zwnr86b7ahz">Settings</h1>
        <dl className="mt-6 grid gap-x-10 gap-y-3 text-[15px] sm:grid-cols-[140px_1fr]" data-id="element-1878" data-mp="37l7xlhri9pz4">
          <dt className="text-muted" data-id="element-1879" data-mp="33kfqau271v5h">Name</dt>
          <dd className="text-ink" data-id="element-1880" data-mp="2ym4eidsc9akm">{state.learner.name}</dd>
          <dt className="text-muted" data-id="element-1881" data-mp="2yoqmgej55s3d">Email</dt>
          <dd className="text-ink" data-id="element-1882" data-mp="2toco5o7cdwij">{state.learner.email}</dd>
          <dt className="text-muted" data-id="element-1883" data-mp="2tpw4fet007kb">Country</dt>
          <dd className="text-ink" data-id="element-1884" data-mp="3ra9zn1nb11kz">{state.learner.country}</dd>
        </dl>

        <section aria-labelledby="interests" className="mt-12 border-t border-line pt-8" data-id="element-1885" data-mp="3mcgsa5a9m1j4">
          <h2 id="interests" className="text-[17px] font-medium text-ink" data-id="element-1886" data-mp="3hl8mdj858yap">
            Interests
          </h2>
          <p className="mt-1 text-[14px] text-body" data-id="element-1887" data-mp="3cmxdpbtl4tu2">These shape the courses suggested on your dashboard.</p>
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-1888" data-mp="dwosdw42ea1b">
            {INTERESTS.map(i => {
            const on = hub.interests.includes(i.id);
            return <button key={i.id} type="button" aria-pressed={on} onClick={() => toggleInterest(i.id)} className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[14px] transition-colors duration-150 ${on ? 'border-gold bg-charcoal text-ink' : 'border-line text-body hover:border-gold/50'}`} data-id="element-1889" data-mp="gguya9c8oaki">
                  {on && <CheckIcon className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-1890" data-mp="zfflvrex8xte" />}
                  {i.label}
                </button>;
          })}
          </div>
        </section>

        <section aria-labelledby="email" className="mt-12 border-t border-line pt-8" data-id="element-1891" data-mp="196ah3la5b7qz">
          <h2 id="email" className="text-[17px] font-medium text-ink" data-id="element-1892" data-mp="1e42bqn26d7yv">
            Email notifications
          </h2>
          <p className="mt-1 text-[14px] text-body" data-id="element-1893" data-mp="1j1lofij85cop">In-app notifications are always on. Emails send once the Academy server is connected.</p>
          <ul className="mt-4 divide-y divide-line" data-id="element-1894" data-mp="2ktmu5x6rq8d2">
            {EMAIL_PREFS.map(p => {
            const on = hub.emailPrefs[p.key];
            return <li key={p.key} className="flex items-center justify-between gap-6 py-4" data-id="element-1895" data-mp="2ft8xtcej4muv">
                  <div data-id="element-1896" data-mp="2tokqle94k395">
                    <p id={`pref-${p.key}`} className="text-[15px] text-ink" data-id="element-1897" data-mp="2pl4xgrw9kl17">
                      {p.label}
                    </p>
                    <p className="text-small text-muted" data-id="element-1898" data-mp="21wbvoesyniyo">{p.detail}</p>
                  </div>
                  <button type="button" role="switch" aria-checked={on} aria-labelledby={`pref-${p.key}`} onClick={() => setEmailPref(p.key, !on)} className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${on ? 'bg-gold' : 'bg-line'}`} data-id="element-1899" data-mp="1wx93huy2x784">
                    <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-ink transition-transform duration-150 ease-out ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} data-id="element-1900" data-mp="3hb11upsltmn0" />
                  </button>
                </li>;
          })}
          </ul>
        </section>
      </Container>
    </>;
}